// The DURABLE cool a provider refusal earns on the key's row — `markKeyCooling`'s
// `(reason, retryAt)`, which `cron.byok_reverify` then owns: it re-probes the key at
// `retryAt` and promotes it the moment the provider answers again. Pure, so every
// in-cluster caller that meets a refusal on a key it picked (the in-process lanes,
// `apps/worker` `dispatch/in-process-model.ts`) grades it the way the box's `key_failed`
// ingest does (`apps/worker` `fleet/key-failure-ingest.ts` `keyRowRetryAt`).
//
//   - a WALL (`credits_exhausted` — a 402, or a 429 carrying an exhaustion marker) cools
//     until the provider's STATED reset when it trusts one, else for
//     {@link KEY_ROW_UNDATED_WALL_RECHECK_MS} and then the probe re-checks;
//   - a `rate_limited` throttle cools only when it DATES its reset (a router pool's
//     `earliest reset …`): a dated window is not a request rate, and an undated one is
//     the in-step gate's and the 60 s step-aside's, never a row write;
//   - anything else is not a key fact and earns nothing.

import { verifyRecoverDelayMs } from '../../entities/byok/verify-failure';
import {
  classifyLlmFailure,
  LlmKeyFailureReason,
  type LlmKeyRowCoolingReason,
  llmThrottleResetAt,
} from './failure';
import { parseLlmQuotaResetAt, trustedQuotaReset } from './quota-reset';

/**
 * How long a key wall that STATES NO RESET (`kimi:usage-limit-5-hour`, a bare 402) stays
 * cooled before the verify probe re-checks it — the probe, not a guess, decides when it
 * is back. THE FAILURE IT PREVENTS (`docs/standards/agent-budgets.md`): with no row cool
 * the key is re-picked into the same wall on every call for the whole window (5 h of dead
 * screens, 2026-10-09); a guessed ban the length of the longest window (a week) would idle
 * a key whose 5-hour window lifted long before. One hour, the verify sweep's own recovery
 * delay for a spent wallet (`verifyRecoverDelayMs`), so a key cooled here and one cooled
 * by a probe come back on the same clock.
 */
export const KEY_ROW_UNDATED_WALL_RECHECK_MS = verifyRecoverDelayMs(
  LlmKeyFailureReason.CreditsExhausted,
);

/**
 * The row cool a refusal with this `status` and `body` earns at `now`, or `null` when none.
 * `stated` is true when `retryAt` is the refusal's own trusted reset, false when it is the
 * {@link KEY_ROW_UNDATED_WALL_RECHECK_MS} re-check — a per-MODEL park takes only a stated
 * one (`modelParkOf`: an undated park would be a guess at how long a quota lasts).
 */
export function keyRowCoolOf(
  status: number,
  body: string,
  now: Date,
): {
  readonly reason: LlmKeyRowCoolingReason;
  readonly retryAt: Date;
  readonly stated: boolean;
} | null {
  const reason = classifyLlmFailure(status, body);
  if (reason === null) return null;
  const statedIso = llmThrottleResetAt(body);
  const stated =
    parseLlmQuotaResetAt(body, now) ?? (statedIso === null ? null : new Date(statedIso));
  const trusted = stated === null ? null : trustedQuotaReset(stated, now);
  if (reason === LlmKeyFailureReason.RateLimited) {
    return trusted === null ? null : { reason, retryAt: trusted, stated: true };
  }
  return trusted === null
    ? { reason, retryAt: new Date(now.getTime() + KEY_ROW_UNDATED_WALL_RECHECK_MS), stated: false }
    : { reason, retryAt: trusted, stated: true };
}
